import { fetchEvents, patchEvent } from '../lib/gcal';
import { parseFOHMeta } from '../api/rules';
import { stripAssigneeTag, getEventAssignee, buildFOHDescription } from '../api/assignments';
import { standardizeTitle } from './titleStandardize';

// Reset & Data → "Standardize all titles" — the same cleanup Recurring Events
// offers, but over a full year instead of that screen's 60-day window, so it
// actually reaches "everything" rather than just what's coming up soon.
const WINDOW_DAYS = 365;
const CHUNK_SIZE = 5; // small concurrency limit — sequential chunks, not a full Promise.all

function isReadOnlyCalendar(calMetaById, calId, sampleEvent) {
  const role = calMetaById[calId]?.accessRole;
  if (role) return role === 'reader' || role === 'freeBusyReader';
  return sampleEvent?.organizer?.self === false;
}

// Scans every connected calendar for recurring series whose title would
// change under standardizeTitle() — mirrors RecurringDefaults' candidate
// predicate (writable calendars, non-reminder series only), just widened.
export async function findStandardizeCandidates(familyData) {
  const calAssignments = familyData?.config?.calendarAssignments || {};
  const suppressedCals = familyData?.config?.suppressedCalendars || [];
  const calendarMeta   = familyData?.config?.calendarMeta || [];
  const calMetaById = calendarMeta.reduce((acc, m) => { acc[m.id] = m; return acc; }, {});
  const carePartners = familyData?.carePartners || [];
  const children      = familyData?.children || [];
  const sitters        = familyData?.sitters || [];

  const calIds = Object.keys(calAssignments).filter(id => !suppressedCals.includes(id));
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const end   = new Date(start); end.setDate(end.getDate() + WINDOW_DAYS); end.setHours(23, 59, 59, 999);

  const all = [];
  for (const calId of calIds) {
    try {
      const evts = await fetchEvents({ calendarId: calId, timeMin: start.toISOString(), timeMax: end.toISOString(), maxResults: 250 });
      for (const e of (evts || [])) if (e.recurringEventId) all.push({ ...e, _calendarId: calId });
    } catch (err) {
      console.warn('[standardizeAllTitles] cal fetch failed:', calId, err.message);
    }
  }

  const byMaster = {};
  for (const e of all) {
    if (!byMaster[e.recurringEventId]) byMaster[e.recurringEventId] = [];
    byMaster[e.recurringEventId].push(e);
  }

  const candidates = [];
  for (const instances of Object.values(byMaster)) {
    const sample = [...instances].sort((a, b) =>
      (a.start?.dateTime || a.start?.date || '').localeCompare(b.start?.dateTime || b.start?.date || '')
    )[0];
    if (isReadOnlyCalendar(calMetaById, sample._calendarId, sample)) continue;

    const meta = parseFOHMeta(sample.description);
    if (meta?.reminder === true) continue; // reminder — already clean/tag-free

    const title = stripAssigneeTag(sample.summary || '(No title)');
    const newTitle = standardizeTitle(title, { carePartners, children, sitters, assigneeCode: meta?.assignee || null });
    if (newTitle === title) continue;

    candidates.push({ recurringEventId: sample.recurringEventId, calendarId: sample._calendarId, title, newTitle, assignee: meta?.assignee || null });
  }

  return candidates;
}

// Applies standardizeTitle to every candidate found by findStandardizeCandidates,
// in small chunks so a busy family calendar doesn't fire dozens of concurrent
// GCal writes at once. Reports progress via onProgress(done, total).
export async function applyStandardizeAll(familyData, { onProgress } = {}) {
  const candidates = await findStandardizeCandidates(familyData);
  const results = [];
  for (let i = 0; i < candidates.length; i += CHUNK_SIZE) {
    const chunk = candidates.slice(i, i + CHUNK_SIZE);
    const chunkResults = await Promise.all(chunk.map(async (c) => {
      try {
        const resolvedCode = getEventAssignee({ summary: c.newTitle }) || c.assignee;
        const description = buildFOHDescription('', { assignee: resolvedCode, reminder: false, reviewed: true });
        await patchEvent(c.calendarId, c.recurringEventId, { summary: c.newTitle, description });
        return { ...c, status: 'updated' };
      } catch (err) {
        return { ...c, status: 'failed', reason: err.message };
      }
    }));
    results.push(...chunkResults);
    onProgress?.(results.length, candidates.length);
  }
  return results;
}
